import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaMapMarkerAlt, FaEdit } from 'react-icons/fa';
import API_CONFIG from './config';
import './styles/waste_dropoff_sites.css';

interface DropoffSite {
    id: number;
    name: string;
    address: string;
    display_address?: string;
    is_active: boolean;
    permit_number?: string;
    ticket_count?: number;
    email?: string;
}

interface WasteDropoffSitesProps {
    user: { job_role: string; name: string; user_id: number; site_ids?: string[] };
}

const WasteDropoffSites: React.FC<WasteDropoffSitesProps> = ({ user }) => {
    const [sites, setSites] = useState<DropoffSite[]>([]);
    const navigate = useNavigate();
    const [checkingSession, setCheckingSession] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [name, setName] = useState('');
    const [fullAddress, setFullAddress] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editSite, setEditSite] = useState<DropoffSite | null>(null);
    const [editName, setEditName] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editActive, setEditActive] = useState(true);
    const [permitNumber, setPermitNumber] = useState('');
    const [editPermitNumber, setEditPermitNumber] = useState('');
    const [addressSuggestions, setAddressSuggestions] = useState<{ address: string; latitude: number; longitude: number }[]>([]);
    const [selectedAddressObj, setSelectedAddressObj] = useState<{ address: string; latitude: number; longitude: number } | null>(null);
    const [showAddressDropdown, setShowAddressDropdown] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState<number>(-1);
    const [email, setEmail] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editDisplayAddress, setEditDisplayAddress] = useState('');
    const [displayAddress, setDisplayAddress] = useState('');

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        axios.get('/api/session', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
            .then(() => {
                return axios.get('/api/waste-dropoff-sites?with_ticket_count=1', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            })
            .then(res => {
                if (cancelled) return;
                setSites(res.data);
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 401) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    navigate('/login');
                } else {
                    setSites([]);
                }
            })
            .finally(() => {
                if (!cancelled) setCheckingSession(false);
            });
        return () => { cancelled = true; };
    }, [showDialog, message, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        if (!name.trim() || !selectedAddressObj || !permitNumber.trim()) {
            setError('All fields are required and you must select a valid address.');
            return;
        }
        try {
            await axios.post('/api/waste-dropoff-sites', {
                name,
                address: selectedAddressObj.address,
                is_active: isActive,
                display_address: displayAddress,
                permit_number: permitNumber,
                latitude: selectedAddressObj.latitude,
                longitude: selectedAddressObj.longitude,
                email
            }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setMessage('Dropoff site created!');
            setName('');
            setFullAddress('');
            setPermitNumber('');
            setDisplayAddress('');
            setIsActive(true);
            setShowDialog(false);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || 'Failed to create site');
        }
    };

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFullAddress(e.target.value);
        setSelectedAddressObj(null);
        setActiveSuggestion(-1);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        if (e.target.value.length >= 4) {
            debounceTimeout.current = setTimeout(() => {
                fetchAddressesByAddress(e.target.value);
            }, 1000);
        } else {
            setAddressSuggestions([]);
            setShowAddressDropdown(false);
        }
    };

    const fetchAddressesByAddress = async (address: string) => {
        if (!address) return;
        try {
            const res = await axios.get(
                `/api/addresses-by-address?address=${encodeURIComponent(address)}`,
                { withCredentials: true, baseURL: API_CONFIG.BASE_URL }
            );
            setAddressSuggestions(res.data || []);
            setShowAddressDropdown(true);
        } catch {
            setAddressSuggestions([]);
            setShowAddressDropdown(false);
        }
    };

    const handleEditClick = (site: DropoffSite) => {
        setEditSite(site);
        setEditName(site.name);
        setEditAddress(site.address);
        setEditActive(site.is_active);
        setEditPermitNumber(site.permit_number || '');
        setEditEmail(site.email ?? '');
        setEditDisplayAddress(site.display_address || ''); // Add this line
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (!editSite) return;
        try {
            await axios.put(`/api/waste-dropoff-sites/${editSite.id}`, {
                name: editName,
                address: editAddress,
                display_address: editDisplayAddress, // Add this line
                is_active: editActive,
                permit_number: editPermitNumber,
                email: editEmail
            }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setMessage('Site updated!');
            setEditDialogOpen(false);
            setEditSite(null);
            
            axios.get('/api/waste-dropoff-sites?with_ticket_count=1', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
                .then(res => setSites(res.data))
                .catch(() => setSites([]));
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || 'Failed to update site');
        }
    };

    const CARD_WIDTH = 350;
    const CARD_HEIGHT = 340;

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                background: '#f5f6fa',
                display: 'flex',
                flexDirection: 'column',
                padding: '40px',
                boxSizing: 'border-box'
            }}
            className="dropoff-sites-main-container"
        >
            {/* Header */}
            <div className="dropoff-sites-header">
                <div className="dropoff-sites-title">
                    <h2 style={{
                        margin: 0,
                        fontSize: '2.4em',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        color: '#333'
                    }}>
                        Waste Dropoff Sites
                    </h2>
                </div>
                <div className="create-button-container">
                    {user.job_role === 'Admin' && (
                        <button
                            style={{
                                background: '#115740',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '10px 22px',
                                fontWeight: 600,
                                fontSize: '1em',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(17,87,64,0.3)'
                            }}
                            onClick={() => setShowDialog(true)}
                        >
                            + Add Dropoff Site
                        </button>
                    )}
                </div>
            </div>
            {/* Grid */}
            <div className="dropoff-sites-grid">
                {sites.length === 0 ? (
                    <div style={{
                        color: '#666',
                        textAlign: 'center',
                        padding: 32,
                        width: '100%',
                        gridColumn: '1/-1',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        No dropoff sites found.
                    </div>
                ) : (
                    Array.from({ length: Math.max(sites.length, 4) }).map((_, idx) => {
                        const site = sites[idx];
                        if (!site) {
                            return <div key={`empty-${idx}`} style={{ width: CARD_WIDTH, height: CARD_HEIGHT, visibility: 'hidden' }} />;
                        }
                        return (
                            <div
                                key={site.id}
                                className="dropoff-site-card"
                                style={{
                                    background: '#fff',
                                    borderRadius: 12,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    padding: 28,
                                    width: CARD_WIDTH,
                                    height: CARD_HEIGHT,
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {/* Circle initials */}
                                <div
                                    className="card-avatar"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        background: '#115740',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '1.2em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'absolute',
                                        top: 22,
                                        left: 22
                                    }}
                                >
                                    {site.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                                </div>
                                {/* Edit icon only for Admin */}
                                <div style={{
                                    position: 'absolute',
                                    top: 22,
                                    right: 22,
                                    display: 'flex',
                                    gap: 16
                                }}>
                                    {user.job_role === 'Admin' && (
                                        <FaEdit
                                            className="card-edit-icon"
                                            style={{ color: '#888', cursor: 'pointer', fontSize: '1.1em' }}
                                            title="Edit"
                                            onClick={() => handleEditClick(site)}
                                        />
                                    )}
                                </div>
                                <div style={{
                                    width: '90%',
                                    margin: '0 auto',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flex: 1,
                                    justifyContent: 'center'
                                }}>
                                    {/* Name and status */}
                                    <div
                                        className="card-title"
                                        style={{
                                            fontWeight: 700,
                                            fontSize: '1.1em',
                                            color: '#115740',
                                            marginTop: 54,
                                            marginBottom: 10,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            overflow: 'visible',
                                            textOverflow: 'unset'
                                        }}
                                    >
                                        {site.name}
                                    </div>
                                    {/* Address */}
                                    <div
                                        className="card-address"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 10,
                                            color: '#444',
                                            fontSize: '0.9em',
                                            marginBottom: 10,
                                            width: '100%',
                                        }}
                                    >
                                        <FaMapMarkerAlt
                                            style={{
                                                color: '#115740',
                                                fontSize: '1.1em',
                                                flexShrink: 0,
                                                alignSelf: 'flex-start',
                                                marginTop: 2
                                            }}
                                        />
                                        <span style={{
                                            textAlign: 'center',
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-line',
                                            width: '100%',
                                            display: 'block'
                                        }}>
                                            {site.display_address}
                                        </span>
                                    </div>
                                    {/* Permit Number */}
                                    <div
                                        className="card-permit"
                                        style={{
                                            color: '#444',
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            marginBottom: 10
                                        }}
                                    >
                                        Permit Number: {site.permit_number ?? '—'}
                                    </div>
                                    {/* Status */}
                                    <div
                                        className="card-status"
                                        style={{
                                            color: site.is_active ? '#115740' : '#d32f2f',
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            marginTop: 10,
                                            marginBottom: 10
                                        }}
                                    >
                                        {site.is_active ? 'Active' : 'Inactive'}
                                    </div>
                                    {/* Divider */}
                                    <hr style={{ width: '70%', margin: '18px auto 12px auto', border: 'none', borderTop: '1px solid #eee' }} />
                                    {/* Waste tickets count */}
                                    <div
                                        className="card-ticket-count"
                                        style={{
                                            fontSize: '1em',
                                            color: '#444',
                                            marginTop: 10
                                        }}
                                    >
                                        Waste tickets associated: <span style={{ fontWeight: 700 }}>{site.ticket_count ?? 0}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Edit Dialog */}
            {editDialogOpen && editSite && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="dropoff-edit-dialog" style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Edit Dropoff Site</h3>
                        <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <input
                                type="text"
                                placeholder="Site Name"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Site Address"
                                value={editAddress}
                                onChange={e => setEditAddress(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Display Address"
                                value={editDisplayAddress}
                                onChange={e => setEditDisplayAddress(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Permit Number"
                                value={editPermitNumber}
                                onChange={e => setEditPermitNumber(e.target.value)}
                                required
                            />
                            <input
                                type="email"
                                placeholder="Site Email Address"
                                value={editEmail}
                                onChange={e => setEditEmail(e.target.value)}
                            />
                            <label>
                                <input
                                    type="checkbox"
                                    checked={editActive}
                                    onChange={e => setEditActive(e.target.checked)}
                                />
                                Active
                            </label>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#115740',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 600
                                    }}
                                    onClick={handleEditSave}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#eee',
                                        color: '#333',
                                        border: 'none',
                                        fontWeight: 500
                                    }}
                                    onClick={() => {
                                        setEditDialogOpen(false);
                                        setEditSite(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}

            {/* Create Dialog */}
            {showDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="dropoff-dialog" style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320, position: 'relative'
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Create Waste Dropoff Site</h3>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <input
                                type="text"
                                placeholder="Site Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="Full Address"
                                    value={fullAddress}
                                    onChange={handleAddressChange}
                                    onFocus={() => {
                                        if (addressSuggestions.length > 0) setShowAddressDropdown(true);
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setShowAddressDropdown(false), 150);
                                    }}
                                    onKeyDown={e => {
                                        if (addressSuggestions.length > 0) {
                                            if (e.key === 'ArrowDown') {
                                                setActiveSuggestion(prev => Math.min(prev + 1, addressSuggestions.length - 1));
                                            } else if (e.key === 'ArrowUp') {
                                                setActiveSuggestion(prev => Math.max(prev - 1, 0));
                                            } else if (e.key === 'Enter' && activeSuggestion >= 0) {
                                                const selected = addressSuggestions[activeSuggestion];
                                                setSelectedAddressObj(selected);
                                                setFullAddress(selected.address);
                                                setShowAddressDropdown(false);
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                    style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                                    required
                                    autoComplete="off"
                                />
                                {showAddressDropdown && addressSuggestions.length > 0 && (
                                    <ul
                                        style={{
                                            listStyle: 'none',
                                            margin: 0,
                                            padding: 0,
                                            position: 'absolute',
                                            background: '#fff',
                                            border: '1px solid #ccc',
                                            borderRadius: 6,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                            zIndex: 1001,
                                            width: '100%',
                                            maxHeight: 180,
                                            overflowY: 'auto',
                                            top: 44,
                                            left: 0
                                        }}
                                    >
                                        {addressSuggestions.map((suggestion, idx) => (
                                            <li
                                                key={suggestion.address}
                                                style={{
                                                    padding: '10px 16px',
                                                    background: idx === activeSuggestion ? '#e6f2ed' : '#fff',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={() => setActiveSuggestion(idx)}
                                                onMouseLeave={() => setActiveSuggestion(-1)}
                                                onMouseDown={() => {
                                                    setSelectedAddressObj(suggestion);
                                                    setFullAddress(suggestion.address);
                                                    setShowAddressDropdown(false);
                                                }}
                                            >
                                                {suggestion.address}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <input
                                type="text"
                                placeholder="Display Address"
                                value={displayAddress}
                                onChange={e => setDisplayAddress(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Permit Number"
                                value={permitNumber}
                                onChange={e => setPermitNumber(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="email"
                                placeholder="Site Email Address"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={e => setIsActive(e.target.checked)}
                                />
                                Active
                            </label>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: selectedAddressObj ? '#115740' : '#ccc',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 600,
                                        cursor: selectedAddressObj ? 'pointer' : 'not-allowed'
                                    }}
                                    disabled={!selectedAddressObj}
                                >
                                    Create Site
                                </button>
                                <button type="button" style={{
                                    padding: '10px 22px',
                                    borderRadius: 6,
                                    background: '#eee',
                                    color: '#333',
                                    border: 'none',
                                    fontWeight: 500
                                }} onClick={() => setShowDialog(false)}>
                                    Cancel
                                </button>
                            </div>
                            {message && <div style={{ color: '#115740', fontWeight: 500 }}>{message}</div>}
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WasteDropoffSites;